import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import axios from "axios";
import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowUpRight, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { SECTORS, FACILITY_TYPES, FREQUENCIES } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FLOOR_TYPES = ["VCT / Hard floor", "Tile & grout", "Carpet", "Concrete", "Hardwood", "Polished / stone"];

const schema = z.object({
  name: z.string().min(2, "Please enter your full name"),
  company: z.string().min(2, "Please enter your company name"),
  job_title: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email("Please enter a valid email address"),
  facility_address: z.string().optional(),
  service_area: z.string().optional(),
  facility_type: z.string().optional(),
  square_footage: z.string().optional(),
  restroom_count: z.string().optional(),
  floor_types: z.array(z.string()).optional(),
  frequency: z.string().optional(),
  cleaning_challenges: z.string().optional(),
  special_instructions: z.string().optional(),
  walkthrough_datetime: z.string().optional(),
});

const fieldBase =
  "w-full border border-border bg-white px-4 py-3 text-sm text-navy transition-colors duration-200 placeholder:text-steel/60 focus:border-brand-green focus:outline-none";
const labelBase = "mb-2 block font-display text-xs font-semibold uppercase tracking-wider text-navy";

const Field = ({ label, error, required, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className={labelBase}>
      {label} {required && <span className="text-brand-green-ink">*</span>}
    </span>
    {children}
    {error && (
      <span className="mt-2 flex items-center gap-1.5 text-xs text-destructive" role="alert">
        <AlertCircle size={13} /> {error.message}
      </span>
    )}
  </label>
);

const SectionTitle = ({ step, title }) => (
  <div className="flex items-center gap-4 border-b border-border pb-4">
    <span className="flex h-9 w-9 items-center justify-center bg-navy font-display text-sm font-black text-white">{step}</span>
    <h2 className="font-display text-lg font-bold uppercase tracking-tight text-navy">{title}</h2>
  </div>
);

export default function RequestQuote() {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema), defaultValues: { floor_types: [] } });

  const selectedFloors = watch("floor_types") || [];
  const toggleFloor = (f) => {
    const next = selectedFloors.includes(f) ? selectedFloors.filter((x) => x !== f) : [...selectedFloors, f];
    setValue("floor_types", next, { shouldValidate: false });
  };

  const onSubmit = async (data) => {
    try {
      await axios.post(`${API}/quotes`, data);
      setSubmitted(true);
      reset();
      toast.success("Your facility assessment request has been received.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Something went wrong. Please try again or contact our operations team.");
    }
  };

  return (
    <>
      <Seo
        title="Request a Quote"
        description="Request a customized commercial cleaning plan. Share your facility details and our operations team will build a scope around exactly how you want it cleaned."
        path="/request-a-quote"
      />
      <PageHero eyebrow="Request a Quote" title={["Request a customized", "cleaning plan."]} />

      <section className="container-px mx-auto max-w-[1500px] py-16 lg:py-20">
        <div className="grid gap-14 lg:grid-cols-[0.85fr_1.6fr]">
          {/* Sidebar */}
          <aside className="lg:sticky lg:top-28 lg:self-start">
            <h2 className="font-display text-2xl font-bold tracking-tight text-navy">A facility assessment, not a form.</h2>
            <ol className="mt-8 flex flex-col gap-6">
              {[
                "Share your facility profile and priorities",
                "We schedule a facility walkthrough",
                "We deliver a written, customized scope & quote",
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="flex h-8 w-8 flex-none items-center justify-center bg-brand-green font-display text-sm font-black text-navy">
                    {i + 1}
                  </span>
                  <span className="pt-1 text-sm leading-relaxed text-steel">{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-10 border border-border p-6">
              <p className="overline">We serve</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {SECTORS.map((s) => (
                  <span key={s} className="border border-border px-3 py-1.5 text-xs font-medium text-navy">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <Link to="/contact" data-testid="operations-link" className="mt-6 inline-flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-wider text-navy hover:text-brand-green-ink">
              Talk With Our Operations Team <ArrowUpRight size={14} />
            </Link>
          </aside>

          {/* Form */}
          <div>
            {submitted ? (
              <motion.div
                data-testid="quote-success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-col items-start border border-brand-green/40 bg-brand-green/10 p-10 sm:p-14"
              >
                <CheckCircle2 size={44} className="text-brand-green-ink" />
                <h2 className="mt-6 font-display text-3xl font-bold tracking-tight text-navy">Request received.</h2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-steel">
                  Thank you — your facility details have been logged and routed to our operations team. We'll follow up to
                  schedule your walkthrough.
                </p>
                <button data-testid="quote-reset" onClick={() => setSubmitted(false)} className="btn-ghost mt-8">
                  Submit another request <ArrowUpRight size={16} />
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} data-testid="quote-form" noValidate className="flex flex-col gap-12">
                {/* Section 1 */}
                <div className="flex flex-col gap-6">
                  <SectionTitle step="1" title="Your Details" />
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Name" error={errors.name} required>
                      <input data-testid="quote-name" className={fieldBase} placeholder="Jane Doe" {...register("name")} />
                    </Field>
                    <Field label="Company" error={errors.company} required>
                      <input data-testid="quote-company" className={fieldBase} placeholder="Acme Facilities" {...register("company")} />
                    </Field>
                    <Field label="Job Title" error={errors.job_title}>
                      <input data-testid="quote-job-title" className={fieldBase} placeholder="Facilities Manager" {...register("job_title")} />
                    </Field>
                    <Field label="Phone" error={errors.phone}>
                      <input data-testid="quote-phone" className={fieldBase} placeholder="(000) 000-0000" {...register("phone")} />
                    </Field>
                    <Field label="Email" error={errors.email} required className="sm:col-span-2">
                      <input data-testid="quote-email" type="email" className={fieldBase} placeholder="jane@company.com" {...register("email")} />
                    </Field>
                  </div>
                </div>

                {/* Section 2 */}
                <div className="flex flex-col gap-6">
                  <SectionTitle step="2" title="Facility Profile" />
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Facility address" error={errors.facility_address} className="sm:col-span-2">
                      <input data-testid="quote-address" className={fieldBase} placeholder="Street, City, State, ZIP" {...register("facility_address")} />
                    </Field>
                    <Field label="Service area" error={errors.service_area}>
                      <input data-testid="quote-service-area" className={fieldBase} placeholder="e.g. Northern NJ / NYC metro" {...register("service_area")} />
                    </Field>
                    <Field label="Facility type" error={errors.facility_type}>
                      <select data-testid="quote-facility-type" className={fieldBase} {...register("facility_type")}>
                        <option value="">Select…</option>
                        {FACILITY_TYPES.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Approximate square footage" error={errors.square_footage}>
                      <input data-testid="quote-sqft" className={fieldBase} placeholder="e.g. 40,000 sq ft" {...register("square_footage")} />
                    </Field>
                    <Field label="Restroom count" error={errors.restroom_count}>
                      <input data-testid="quote-restrooms" className={fieldBase} placeholder="e.g. 6" {...register("restroom_count")} />
                    </Field>
                  </div>
                  <div>
                    <span className={labelBase}>Floor types</span>
                    <div className="grid gap-px overflow-hidden border border-border bg-border sm:grid-cols-3">
                      {FLOOR_TYPES.map((f) => {
                        const active = selectedFloors.includes(f);
                        return (
                          <button
                            type="button"
                            key={f}
                            data-testid={`quote-floor-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                            onClick={() => toggleFloor(f)}
                            aria-pressed={active}
                            className={`flex items-center justify-between gap-2 px-4 py-3 text-left text-sm transition-colors duration-200 ${
                              active ? "bg-navy text-white" : "bg-white text-navy hover:bg-cool-gray"
                            }`}
                          >
                            {f}
                            <span className={`h-3.5 w-3.5 flex-none border ${active ? "border-white bg-brand-green" : "border-navy/30"}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Section 3 */}
                <div className="flex flex-col gap-6">
                  <SectionTitle step="3" title="Tell Us Exactly How You Want It Cleaned" />
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Desired cleaning frequency" error={errors.frequency}>
                      <select data-testid="quote-frequency" className={fieldBase} {...register("frequency")}>
                        <option value="">Select…</option>
                        {FREQUENCIES.map((f) => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Preferred walkthrough date/time" error={errors.walkthrough_datetime}>
                      <input data-testid="quote-walkthrough" className={fieldBase} placeholder="e.g. Weekday mornings" {...register("walkthrough_datetime")} />
                    </Field>
                  </div>
                  <Field label="Current cleaning challenges" error={errors.cleaning_challenges}>
                    <textarea data-testid="quote-challenges" rows={4} className={fieldBase} placeholder="What isn't working with your current program?" {...register("cleaning_challenges")} />
                  </Field>
                  <Field label="Special instructions or compliance needs" error={errors.special_instructions}>
                    <textarea data-testid="quote-instructions" rows={4} className={fieldBase} placeholder="Security, compliance, access, standards, priorities…" {...register("special_instructions")} />
                  </Field>
                </div>

                <button type="submit" data-testid="quote-submit" disabled={isSubmitting} className="btn-primary w-full justify-center sm:w-fit disabled:opacity-60">
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Get a Commercial Cleaning Quote <ArrowUpRight size={16} />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
